import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Server, Cloud, Puzzle, ChevronLeft, ChevronRight, Cpu, ShieldCheck, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const slideImages = [
  '/Selection_240.png',
  '/Selection_243.png',
  '/Selection_244.png',
  '/Selection_245.png',
  '/Selection_246.png',
  '/Selection_247.png',
];

const pillarIcons = [Server, Cloud, Puzzle];

const chips = [
  { icon: Cpu, label: 'Whisper · Llama · Mistral', className: '-left-6 top-10' },
  { icon: ShieldCheck, label: 'GDPR · EU', className: '-right-4 top-1/3' },
  { icon: Sparkles, label: 'Zimbra 10', className: 'left-10 -bottom-5' },
];

const Hero = () => {
  const { t } = useTranslation('common');
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);

  const slides = t('hero.slides', { returnObjects: true }) as Array<{ label: string; caption: string }>;
  const pillars = t('hero.pillars', { returnObjects: true }) as Array<{ title: string; description: string }>;
  const safePillars = Array.isArray(pillars) ? pillars : [];
  const safeSlides = Array.isArray(slides) ? slides : [];

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => setCurrent((c) => (c + 1) % slideImages.length), 4500);
    return () => clearInterval(id);
  }, [paused]);

  const prev = () => setCurrent((c) => (c - 1 + slideImages.length) % slideImages.length);
  const next = () => setCurrent((c) => (c + 1) % slideImages.length);

  return (
    <section className="relative bg-slate-950 overflow-hidden">
      <div className="absolute inset-0 bg-grid-dark mask-fade-b" />
      <div className="pointer-events-none absolute -top-40 left-[10%] w-[640px] h-[640px] rounded-full bg-blue-600/25 blur-[120px] animate-aurora" />
      <div className="pointer-events-none absolute top-20 right-[-10%] w-[560px] h-[560px] rounded-full bg-cyan-500/20 blur-[120px] animate-aurora [animation-delay:-7s]" />
      <div className="pointer-events-none absolute bottom-[-20%] left-1/3 w-[520px] h-[420px] rounded-full bg-emerald-500/10 blur-[120px]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 lg:pt-24 pb-24">
        <div className="grid lg:grid-cols-[1.05fr_1fr] gap-14 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: 'easeOut' }}>
            <div className="inline-flex items-center gap-2 pl-1.5 pr-4 py-1.5 rounded-full border border-cyan-400/25 bg-cyan-400/[0.06] mb-8">
              <span className="relative flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-cyan-400 to-emerald-400">
                <span className="absolute inset-0 rounded-full bg-cyan-400 animate-ping opacity-40" />
                <Sparkles className="relative h-3.5 w-3.5 text-slate-950" />
              </span>
              <span className="text-cyan-200 text-sm font-medium">{t('hero.badge')}</span>
            </div>

            <h1 className="font-display text-[2.6rem] sm:text-6xl lg:text-[4.25rem] font-bold text-white leading-[1.05] mb-7">
              {t('hero.title1')}{' '}
              <span className="text-gradient animate-shimmer">{t('hero.titleZimlets')}</span>
              <span className="text-slate-500"> &amp; </span>
              <span className="text-gradient animate-shimmer">{t('hero.titleServices')}</span>
            </h1>

            <p className="text-lg text-slate-300/90 leading-relaxed mb-10 max-w-xl">{t('hero.description')}</p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/products/zimbra-sovereign-ai"
                className="group relative inline-flex items-center justify-center gap-2 overflow-hidden px-7 py-4 rounded-full font-semibold text-slate-950 bg-gradient-to-r from-cyan-300 via-sky-400 to-emerald-300 shadow-[0_0_40px_-8px_rgba(34,211,238,0.8)] hover:shadow-[0_0_56px_-6px_rgba(34,211,238,1)] transition-shadow"
              >
                <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/50 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
                <span className="relative">{t('hero.exploreAdmin')}</span>
                <ArrowRight className="relative h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/products"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full font-semibold text-white border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] hover:border-white/30 transition-all"
              >
                {t('hero.viewZimlets')}
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30, rotateX: 8 }}
            animate={{ opacity: 1, y: 0, rotateX: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: 'easeOut' }}
            className="relative [perspective:1200px]"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
            <div className="absolute -inset-6 rounded-[2rem] bg-gradient-to-br from-cyan-500/30 via-blue-600/10 to-emerald-400/20 blur-2xl" />
            <div className="relative rounded-2xl p-px bg-gradient-to-br from-cyan-300/60 via-white/10 to-emerald-300/40 shadow-[0_40px_120px_-30px_rgba(8,145,178,0.6)]">
              <div className="rounded-2xl overflow-hidden bg-slate-900">
                <div className="flex items-center gap-2 px-4 py-3 bg-slate-900/90 border-b border-white/10">
                  <span className="w-3 h-3 rounded-full bg-red-400/80" />
                  <span className="w-3 h-3 rounded-full bg-amber-400/80" />
                  <span className="w-3 h-3 rounded-full bg-emerald-400/80" />
                  <div className="flex-1 mx-4 flex items-center gap-2 bg-slate-800/80 rounded-md px-3 py-1 text-xs text-slate-400 font-mono truncate">
                    <ShieldCheck className="h-3 w-3 text-emerald-400 flex-shrink-0" />
                    <span className="truncate">zcspanel.scalenix.com — {safeSlides[current]?.label ?? ''}</span>
                  </div>
                </div>

                <div className="relative aspect-[16/10] bg-slate-900 overflow-hidden">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={current}
                      src={slideImages[current]}
                      alt={safeSlides[current]?.label ?? ''}
                      initial={{ opacity: 0, scale: 1.04 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.98 }}
                      transition={{ duration: 0.45 }}
                      className="w-full h-full object-cover object-top"
                    />
                  </AnimatePresence>
                  <div className="pointer-events-none absolute inset-x-0 top-0 h-1/4 bg-gradient-to-b from-cyan-300/10 to-transparent animate-scan" />

                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-slate-950/90 via-slate-950/50 to-transparent px-5 pt-10 pb-4">
                    <AnimatePresence mode="wait">
                      <motion.p
                        key={current}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="text-white text-sm font-medium"
                      >
                        {safeSlides[current]?.caption ?? ''}
                      </motion.p>
                    </AnimatePresence>
                  </div>

                  <button
                    onClick={prev}
                    aria-label="Previous"
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-slate-950/60 border border-white/10 backdrop-blur flex items-center justify-center text-white hover:bg-slate-950/90 transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    onClick={next}
                    aria-label="Next"
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-slate-950/60 border border-white/10 backdrop-blur flex items-center justify-center text-white hover:bg-slate-950/90 transition-colors"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {chips.map(({ icon: Icon, label, className }, i) => (
              <div
                key={label}
                className={`hidden sm:flex absolute ${className} items-center gap-2 px-3.5 py-2 rounded-full glass-card text-xs font-medium text-slate-100 shadow-xl animate-float`}
                style={{ animationDelay: `${i * -2}s` }}
              >
                <Icon className="h-3.5 w-3.5 text-cyan-300" />
                {label}
              </div>
            ))}

            <div className="flex justify-center gap-1.5 mt-8">
              {slideImages.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  aria-label={`Slide ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === current ? 'w-8 bg-gradient-to-r from-cyan-300 to-emerald-300' : 'w-1.5 bg-slate-600 hover:bg-slate-400'
                  }`}
                />
              ))}
            </div>
          </motion.div>
        </div>

        <div className="mt-20 grid md:grid-cols-3 gap-4">
          {safePillars.map((p, i) => {
            const Icon = pillarIcons[i] ?? Server;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + i * 0.12 }}
                className="glow-ring group glass-card rounded-2xl p-6 hover:-translate-y-1 transition-transform duration-300"
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/30 to-cyan-400/10 border border-white/10 flex items-center justify-center group-hover:from-cyan-400 group-hover:to-emerald-400 transition-all duration-300">
                    <Icon className="h-5 w-5 text-cyan-300 group-hover:text-slate-950 transition-colors" />
                  </span>
                  <h3 className="text-white font-semibold">{p.title}</h3>
                </div>
                <p className="text-sm text-slate-400 leading-relaxed">{p.description}</p>
              </motion.div>
            );
          })}
        </div>
      </div>

      <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
    </section>
  );
};

export default Hero;
